import createClient from "openapi-fetch";
import { fetchAuthSession } from "aws-amplify/auth";
import type { paths } from "@axial3d/axcel-api-client-types";
import { useCorrelationId } from "@/composables/useCorrelationId";
import { getContent } from "@/services/fileCache";
import {
  type FetchInterceptHooks,
  fetchBlobWithIntercept,
} from "@/services/http";
import { trackDownloadSpeed } from "@/utils/sentry";

const options = await getClientConfig();

async function getClientConfig() {
  return await fetch(`${import.meta.env.BASE_URL}api-config.json`)
    .then((response) => response.json())
    .catch(() => undefined);
}

export function getApiBaseUrl() {
  return options.baseUrl;
}

export function getWebSocketBaseUrl() {
  return options.webSocketBaseUrl;
}

export function getEnvironment() {
  return options.environment;
}

export async function getClient() {
  const { correlationId } = useCorrelationId();

  const client = createClient<paths>(options);
  client.use({
    async onRequest({ request }) {
      const session = await fetchAuthSession();
      if (session.tokens?.idToken) {
        request.headers.set(
          "Authorization",
          session.tokens?.idToken.toString(),
        );
      }
      request.headers.set("X-Correlation-Id", correlationId.value);
      return request;
    },
  });
  return client;
}

export async function fetchFileContent(
  httpUrl: string,
  caseId: string,
  hooks?: FetchInterceptHooks,
) {
  const url = new URL(httpUrl);
  return getContent(url.pathname, caseId, async () => {
    const startTime = performance.now();
    const response = await fetchBlobWithIntercept(url, hooks ?? {});
    const buffer = await response.arrayBuffer();
    const durationMs = performance.now() - startTime;

    const resourceType = getResourceTypeFromUrl(url.pathname);
    trackDownloadSpeed(buffer.byteLength, durationMs, resourceType);

    return buffer;
  });
}

function getResourceTypeFromUrl(pathname: string): string {
  if (pathname.includes("/meshes/")) return "mesh";
  if (pathname.includes("/stacks/")) return "dicom";
  if (pathname.includes("/segmentations/")) return "segmentation";
  return "unknown";
}
